import React from "react";
import { Form, Row, Col } from "react-bootstrap";
import { Field, ErrorMessage, useFormikContext } from "formik";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import PhotoUpload from "./PhotoUpload";
import { CASTE_OPTIONS } from "../formConfig";
import CommonMobileInput from "../../../components/Common/CommonMobileInput";
import { DATE_FORMAT } from "../../../helpers/dateFormat";
import { sanitizeAadharInput, sanitizePanInput } from "../../../helpers/regex";

const CustomerInfoStep = ({
  disabled,
  userDetailsEditable,
  loanTypes,
  photoUploading,
  photoPreview,
  handlePhotoUpload,
}) => {
  const { t } = useTranslation();
  const { values, setFieldValue, setFieldTouched, touched, errors } = useFormikContext();

  return (
    <div>
      <h5 className="mb-3">{t("customer.add.steps.step1")}</h5>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step1.date")}</Form.Label>
            <div>
              <DatePicker
                name="date"
                wrapperClassName="w-100"
                selected={values.date}
                onChange={(date) => setFieldValue("date", date)}
                dateFormat={DATE_FORMAT.DISPLAY}
                className={`form-control ${touched.date && errors.date ? "is-invalid" : ""}`}
                disabled={disabled}
              />
              <ErrorMessage name="date" component="div" className="invalid-feedback d-block" />
            </div>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step1.referrerBillNo")}</Form.Label>
            <Field name="billNo" disabled={disabled} className="form-control" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step1.loanType")}</Form.Label>
            <Form.Select
              name="loanType"
              value={values.loanType}
              disabled={disabled}
              className={`${touched.loanType && errors.loanType ? "is-invalid" : ""}`}
              onChange={(e) => {
                const selectedValue = e.target.value;
                setFieldValue("loanType", selectedValue);
                const selectedType = loanTypes.find((lt) => lt.value === selectedValue);
                if (selectedType && selectedType.defaultInterest !== undefined) {
                  setFieldValue("interestPerYear", selectedType.defaultInterest.toString());
                }
              }}
            >
              <option value="">{t("customer.add.step1.selectLoanType")}</option>
              {loanTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </Form.Select>
            <ErrorMessage name="loanType" component="div" className="invalid-feedback d-block" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step1.name")}</Form.Label>
            <Field
              name="name"
              disabled={disabled || !userDetailsEditable}
              className={`form-control ${touched.name && errors.name ? "is-invalid" : ""}`}
            />
            <ErrorMessage name="name" component="div" className="invalid-feedback d-block" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label className="required">{t("customer.add.step1.mobileNumber")}</Form.Label>
            <CommonMobileInput
              name="mobileNumber"
              disabled={disabled || !userDetailsEditable}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step1.email")}</Form.Label>
            <Field
              name="email"
              type="email"
              disabled={disabled || !userDetailsEditable}
              placeholder={t("customer.add.step1.emailPlaceholder")}
              className={`form-control ${touched.email && errors.email ? "is-invalid" : ""}`}
            />
            <ErrorMessage name="email" component="div" className="invalid-feedback d-block" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step1.panCard")}</Form.Label>
            <input
              type="text"
              name="panCard"
              value={values.panCard || ""}
              disabled={true}
              className={`form-control bg-light text-muted ${touched.panCard && errors.panCard ? "is-invalid" : ""}`}
              maxLength="10"
              style={{ textTransform: "uppercase", opacity: 1 }}
              onChange={(e) => {
                const value = sanitizePanInput(e.target.value);
                setFieldValue("panCard", value);
                setFieldTouched("panCard", true, false);
              }}
              onBlur={() => setFieldTouched("panCard", true, true)}
            />
            <ErrorMessage name="panCard" component="div" className="invalid-feedback" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step1.aadharCard")}</Form.Label>
            <div className="d-flex gap-2">
              <input
                type="text"
                name="aadharCard"
                value={values.aadharCard || ""}
                disabled={true}
                className={`form-control bg-light text-muted ${touched.aadharCard && errors.aadharCard ? "is-invalid" : ""}`}
                maxLength="12"
                style={{ opacity: 1 }}
                onChange={(e) => {
                  const value = sanitizeAadharInput(e.target.value);
                  setFieldValue("aadharCard", value);
                  setFieldTouched("aadharCard", true, false);
                }}
                onBlur={() => setFieldTouched("aadharCard", true, true)}
                placeholder={t("customer.add.step1.aadharCardPlaceholder")}
              />
            </div>
            <ErrorMessage name="aadharCard" component="div" className="invalid-feedback" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step1.caste")}</Form.Label>
            <Form.Select
              name="caste"
              value={values.caste || ""}
              disabled={disabled || !userDetailsEditable}
              className={`${touched.caste && errors.caste ? "is-invalid" : ""}`}
              onChange={(e) => setFieldValue("caste", e.target.value)}
              onBlur={() => setFieldTouched("caste", true)}
            >
              <option value="">{t("customer.add.step1.selectCaste")}</option>
              {CASTE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {t(option.labelKey)}
                </option>
              ))}
            </Form.Select>
            <ErrorMessage name="caste" component="div" className="invalid-feedback" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step1.occupation")}</Form.Label>
            <Field name="occupation" disabled={disabled || !userDetailsEditable} className="form-control" />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>{t("customer.add.step1.company")}</Form.Label>
            <Field name="company" disabled={disabled || !userDetailsEditable} className="form-control" />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <Form.Group>
            <Form.Label>{t("customer.add.step1.address")}</Form.Label>
            <Field
              as="textarea"
              name="address"
              disabled={disabled || !userDetailsEditable}
              className="form-control"
              rows={3}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={12}>
          <PhotoUpload
            uploading={photoUploading}
            preview={photoPreview || values.photo}
            disabled={disabled}
            onChange={(file) => handlePhotoUpload(file, setFieldValue)}
            label={t("customer.add.step1.uploadPhoto")}
          />
        </Col>
      </Row>
    </div>
  );
};

export default CustomerInfoStep;
